import React, { useEffect, useState } from "react";
import {
  FiPlus,
  FiRefreshCw,
  FiEdit2,
  FiTrash2,
  FiUsers,
} from "react-icons/fi";
import api from "../../utills/api";

const ROLES = [
  "Admin",
  "Counsellor",
  "Staff",
];

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  role: "Counsellor",
  password: "",
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [search, setSearch] = useState("");

  const loadUsers = async () => {
    try {
      setLoading(true);

      const response = await api.get("/organization/users");

      const data =
        response?.data?.data?.users ||
        response?.data?.data ||
        response?.data ||
        [];

      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load users:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const openCreate = () => {
    setEditingUser(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);

    setForm({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      role: user.role || "Counsellor",
      password: "",
    });

    setModalOpen(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveUser = async (event) => {
    event.preventDefault();

    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        role: form.role,
      };

      if (form.password) {
        payload.password = form.password;
      }

      if (editingUser) {
        await api.put(
          `/organization/users/${editingUser._id || editingUser.id}`,
          payload
        );
      } else {
        await api.post("/organization/users", payload);
      }

      setModalOpen(false);
      setForm(EMPTY_FORM);
      await loadUsers();
    } catch (error) {
      console.error("Failed to save user:", error);

      alert(
        error?.response?.data?.message ||
          "Unable to save user."
      );
    }
  };

  const deleteUser = async (user) => {
    const id = user._id || user.id;

    if (!window.confirm(`Delete ${user.name || "this user"}?`)) {
      return;
    }

    try {
      await api.delete(`/organization/users/${id}`);
      await loadUsers();
    } catch (error) {
      console.error("Failed to delete user:", error);

      alert(
        error?.response?.data?.message ||
          "Unable to delete user."
      );
    }
  };

  const filteredUsers = users.filter((user) => {
    const value = search.toLowerCase();

    return (
      !value ||
      user.name?.toLowerCase().includes(value) ||
      user.email?.toLowerCase().includes(value) ||
      user.role?.toLowerCase().includes(value)
    );
  });

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="mb-1">Users & Roles</h4>
            <p className="text-muted mb-0">
              Manage organization users and their roles.
            </p>
          </div>

          <div className="d-flex gap-2">
            <button
              className="btn btn-light"
              onClick={loadUsers}
              disabled={loading}
            >
              <FiRefreshCw size={15} className="me-1" />
              Refresh
            </button>

            <button
              className="btn btn-primary"
              onClick={openCreate}
            >
              <FiPlus size={15} className="me-1" />
              Add User
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="mb-3">
              <input
                type="search"
                className="form-control"
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {loading ? (
              <div className="text-center py-5">
                Loading users...
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td
                          colSpan="6"
                          className="text-center py-5 text-muted"
                        >
                          <FiUsers size={28} className="mb-2" />
                          <div>No users found.</div>
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user) => {
                        const id = user._id || user.id;

                        return (
                          <tr key={id}>
                            <td>
                              <strong>
                                {user.name || "—"}
                              </strong>
                            </td>

                            <td>{user.email || "—"}</td>

                            <td>{user.phone || "—"}</td>

                            <td>
                              <span className="badge bg-light text-dark">
                                {user.role || "—"}
                              </span>
                            </td>

                            <td>
                              <span
                                className={`badge ${
                                  user.isActive === false
                                    ? "bg-danger-subtle text-danger"
                                    : "bg-success-subtle text-success"
                                }`}
                              >
                                {user.isActive === false
                                  ? "Inactive"
                                  : "Active"}
                              </span>
                            </td>

                            <td className="text-end">
                              <button
                                className="btn btn-sm btn-light me-1"
                                onClick={() => openEdit(user)}
                              >
                                <FiEdit2 size={14} />
                              </button>

                              <button
                                className="btn btn-sm btn-light text-danger"
                                onClick={() => deleteUser(user)}
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {modalOpen && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{
              backgroundColor: "rgba(0,0,0,.45)",
            }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <form onSubmit={saveUser}>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {editingUser
                        ? "Edit User"
                        : "Add User"}
                    </h5>

                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setModalOpen(false)}
                    />
                  </div>

                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">
                        Name
                      </label>

                      <input
                        name="name"
                        className="form-control"
                        value={form.name}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Email
                      </label>

                      <input
                        type="email"
                        name="email"
                        className="form-control"
                        value={form.email}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Phone
                      </label>

                      <input
                        name="phone"
                        className="form-control"
                        value={form.phone}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Role
                      </label>

                      <select
                        name="role"
                        className="form-select"
                        value={form.role}
                        onChange={handleChange}
                      >
                        {ROLES.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="form-label">
                        {editingUser
                          ? "New Password (optional)"
                          : "Password"}
                      </label>

                      <input
                        type="password"
                        name="password"
                        className="form-control"
                        value={form.password}
                        onChange={handleChange}
                        required={!editingUser}
                      />
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() => setModalOpen(false)}
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="btn btn-primary"
                    >
                      {editingUser
                        ? "Update User"
                        : "Create User"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;